/**
 * MY Entity — AGM_DUEDATE / AR_DUEDATE 자동 계산 (backend/utils/myEntityDueDateCalculator.js 와 동일 규칙)
 */

export function parseYmd(value: unknown): Date | null {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  const datePart = raw.includes(' ') ? raw.split(' ')[0] : raw;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (!m) {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const day = Number(m[3]);
  const d = new Date(y, mo, day);
  if (d.getFullYear() !== y || d.getMonth() !== mo || d.getDate() !== day) return null;
  return d;
}

export function formatYmd(date: Date | null | undefined): string | null {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function startOfDay(date: Date | string): Date | null {
  const d = date instanceof Date ? date : parseYmd(date);
  if (!d) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addMonths(dateInput: unknown, months: number): Date | null {
  const base = parseYmd(dateInput);
  if (!base || !Number.isFinite(Number(months))) return null;
  const day = base.getDate();
  const result = new Date(base.getFullYear(), base.getMonth() + Number(months), 1);
  const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(day, lastDay));
  return result;
}

export function addDays(dateInput: unknown, days: number): Date | null {
  const base = parseYmd(dateInput);
  if (!base || !Number.isFinite(Number(days))) return null;
  const result = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  result.setDate(result.getDate() + Number(days));
  return result;
}

function addYears(dateInput: unknown, years: number): Date | null {
  return addMonths(dateInput, Number(years) * 12);
}

function minDate(a: Date | null, b: Date | null): Date | null {
  if (!a) return b;
  if (!b) return a;
  return a.getTime() <= b.getTime() ? a : b;
}

export function isPastDue(storedDate: unknown, referenceDate: Date | string = new Date()): boolean {
  const stored = parseYmd(storedDate);
  if (!stored) return true;
  const ref = startOfDay(referenceDate);
  if (!ref) return true;
  return stored.getTime() < ref.getTime();
}

export function shouldAutoCalculateDueDate(
  storedDate: unknown,
  referenceDate: Date | string = new Date(),
  skipManual = false
): boolean {
  if (skipManual) return false;
  return isPastDue(storedDate, referenceDate);
}

function hasLastAgmDate(lastAgmDate: unknown): boolean {
  return !!parseYmd(lastAgmDate);
}

export type MyEntityDueDateInput = {
  incorporationDate?: unknown;
  financialYearEnd?: unknown;
  lastAgmDate?: unknown;
  referenceDate?: Date | string;
};

export function computeAgmDueDate({
  incorporationDate,
  financialYearEnd,
  lastAgmDate,
  referenceDate = new Date(),
}: MyEntityDueDateInput): string | null {
  const ref = startOfDay(referenceDate);
  if (!ref) return null;

  if (!hasLastAgmDate(lastAgmDate)) {
    const firstAgm = addMonths(incorporationDate, 18);
    if (firstAgm) {
      if (firstAgm.getTime() >= ref.getTime()) {
        return formatYmd(firstAgm);
      }
      if (!financialYearEnd) {
        return formatYmd(firstAgm);
      }
    } else if (!financialYearEnd) {
      return null;
    }
  }

  const fyeBase = parseYmd(financialYearEnd);
  if (!fyeBase) return null;

  const lastAgm = hasLastAgmDate(lastAgmDate) ? parseYmd(lastAgmDate) : null;

  for (let i = 0; i < 30; i += 1) {
    const fyeForCycle = i === 0 ? fyeBase : addYears(fyeBase, i);
    if (!fyeForCycle) break;

    const fromFye = addMonths(fyeForCycle, 6);
    let candidate = fromFye;
    if (lastAgm) {
      const fromLastAgm = addMonths(lastAgm, 15);
      candidate = minDate(fromFye, fromLastAgm);
    }
    if (candidate && candidate.getTime() >= ref.getTime()) {
      return formatYmd(candidate);
    }
  }

  const fallback = addMonths(fyeBase, 6);
  return fallback ? formatYmd(fallback) : null;
}

export function computeArDueDate({
  incorporationDate,
  referenceDate = new Date(),
}: Pick<MyEntityDueDateInput, 'incorporationDate' | 'referenceDate'>): string | null {
  const incorp = parseYmd(incorporationDate);
  const ref = startOfDay(referenceDate);
  if (!incorp || !ref) return null;

  let year = ref.getFullYear();
  for (let i = 0; i < 10; i += 1) {
    const anniversary = new Date(year, incorp.getMonth(), incorp.getDate());
    const due = addDays(anniversary, 30);
    if (due && due.getTime() >= ref.getTime()) {
      return formatYmd(due);
    }
    year += 1;
  }
  return null;
}

export type ResolveMyEntityDueDatesInput = {
  incorporationDate?: unknown;
  financialYearEnd?: unknown;
  lastAgmDate?: unknown;
  dbAgmDueDate?: unknown;
  dbArDueDate?: unknown;
  requestAgmDueDate?: unknown;
  requestArDueDate?: unknown;
  skipAgmAutoCalc?: boolean;
  skipArAutoCalc?: boolean;
  referenceDate?: Date | string;
};

export function resolveMyEntityDueDatesForSave(input: ResolveMyEntityDueDatesInput) {
  const {
    incorporationDate,
    financialYearEnd,
    lastAgmDate,
    dbAgmDueDate,
    dbArDueDate,
    requestAgmDueDate,
    requestArDueDate,
    skipAgmAutoCalc = false,
    skipArAutoCalc = false,
    referenceDate = new Date(),
  } = input;

  let agmDueDate = (requestAgmDueDate || dbAgmDueDate || null) as string | null;
  let arDueDate = (requestArDueDate || dbArDueDate || null) as string | null;

  const agmAuto = shouldAutoCalculateDueDate(dbAgmDueDate, referenceDate, skipAgmAutoCalc);
  const arAuto = shouldAutoCalculateDueDate(dbArDueDate, referenceDate, skipArAutoCalc);

  if (agmAuto) {
    const computed = computeAgmDueDate({
      incorporationDate,
      financialYearEnd,
      lastAgmDate,
      referenceDate,
    });
    if (computed) agmDueDate = computed;
  }

  if (arAuto) {
    const computed = computeArDueDate({ incorporationDate, referenceDate });
    if (computed) arDueDate = computed;
  }

  return {
    agmDueDate: agmDueDate || null,
    arDueDate: arDueDate || null,
    agmAutoCalculated: agmAuto && !!agmDueDate,
    arAutoCalculated: arAuto && !!arDueDate,
  };
}

export function extractLastAgmDateFromProfileJson(jsonDataRaw: unknown): string | null {
  if (!jsonDataRaw) return null;
  try {
    const obj =
      typeof jsonDataRaw === 'string'
        ? (JSON.parse(jsonDataRaw) as Record<string, unknown>)
        : (jsonDataRaw as Record<string, unknown>);
    if (!obj || typeof obj !== 'object') return null;
    const v =
      obj.last_agm ??
      obj.lastAgm ??
      obj.lastAgmDate ??
      obj.LAST_AGM ??
      obj.LASTAGM ??
      null;
    const parsed = parseYmd(v);
    return parsed ? formatYmd(parsed) : null;
  } catch {
    return null;
  }
}
